import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle, Clock, Zap, Receipt, Mail, Loader2, Send } from 'lucide-react';

import { API_URL as BACKEND_URL } from '../config/api';
import { METHOD_LABEL } from '../lib/payerFee';
import PayoLogo from './PayoLogo';

const QuickSplitSuccessPage = () => {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const pid = params.get('pid') || '';
  const name = params.get('name') || '';
  const host = params.get('host') || '';
  const desc = params.get('desc') || '';
  const amountCents = parseInt(params.get('amount') || '0', 10);
  const splitCents = parseInt(params.get('split') || '0', 10) || amountCents;
  const feeCents = parseInt(params.get('fee') || '0', 10);
  const totalCents = parseInt(params.get('total') || '0', 10) || amountCents;
  const method = params.get('method') || 'card';
  const isPending = params.get('pending') === 'ach';
  const prefillEmail = params.get('email') || '';

  const [receiptEmail, setReceiptEmail] = useState(prefillEmail);
  const [receiptState, setReceiptState] = useState('idle'); // idle | sending | sent | error

  const fmt = (c) => `$${(Math.abs(c) / 100).toFixed(2)}`;

  // Auto-send receipt if email was provided
  useEffect(() => {
    if (prefillEmail && !isPending && pid) {
      sendReceipt(prefillEmail);
    }
  }, []); // eslint-disable-line

  const sendReceipt = async (email) => {
    if (!email || !email.includes('@')) return;
    setReceiptState('sending');
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-split/public/${sessionId}/send-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          payment_id: pid,
          payer_name: name,
          amount_cents: totalCents,
          host_name: host,
          description: desc,
          split_cents: splitCents,
          fee_cents: feeCents,
          total_cents: totalCents,
          method,
        }),
      });
      if (res.ok) setReceiptState('sent');
      else setReceiptState('error');
    } catch {
      setReceiptState('error');
    }
  };

  const hasBreakdown = feeCents > 0 && totalCents > 0;
  const methodLabel = METHOD_LABEL[method] || 'Card';

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isPending ? 'bg-gradient-to-b from-amber-50 to-white' : 'bg-gradient-to-b from-green-50 to-white'}`}>
      <Card className="max-w-sm w-full shadow-lg" data-testid="qs-success-page">
        <CardContent className="pt-10 pb-8 px-6 text-center">
          <div className="flex flex-col items-center mb-6 space-y-3">
            <PayoLogo size="sm" />
            <span className="text-base font-bold text-gray-800">Quick Split</span>
          </div>

          {isPending ? (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-amber-500" />
              </div>
              <h1 className="text-2xl font-bold text-amber-700 mb-1" data-testid="qs-success-title">Bank Transfer Submitted</h1>
              <p className="text-sm text-gray-500">Your bank transfer is being processed. This typically takes 2–3 business days. We'll update the split once your bank confirms.</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-green-700 mb-1" data-testid="qs-success-title">Payment Complete!</h1>
            </>
          )}

          {name && <p className="text-sm text-gray-500 mt-2">Thank you, <span className="font-medium text-gray-700">{name}</span></p>}
          {totalCents > 0 && <p className="text-3xl font-bold text-gray-900 mt-4" data-testid="qs-success-amount">{fmt(totalCents)}</p>}
          {host && !isPending && <p className="text-sm text-gray-500 mt-1">Paid to <span className="font-medium">{host}</span>'s split</p>}

          {hasBreakdown && (
            <div className="mt-5 bg-gray-50 border border-gray-100 rounded-lg p-3 text-sm text-left space-y-1" data-testid="qs-success-breakdown">
              <div className="flex justify-between text-gray-600"><span>Split amount</span><span data-testid="qs-success-split">{fmt(splitCents)}</span></div>
              <div className="flex justify-between text-gray-500"><span>Processing fee ({methodLabel})</span><span data-testid="qs-success-fee">{fmt(feeCents)}</span></div>
              <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t border-gray-200"><span>Total charged</span><span data-testid="qs-success-total">{fmt(totalCents)}</span></div>
            </div>
          )}

          {pid && (
            <div className="mt-6 bg-gray-50 border border-gray-100 rounded-lg p-4" data-testid="qs-success-receipt">
              <div className="flex items-center justify-center space-x-2 text-xs text-gray-400 mb-1">
                <Receipt className="w-3 h-3" /><span>Payment Reference</span>
              </div>
              <p className="text-base font-mono font-semibold text-gray-800" data-testid="qs-success-pid">{pid}</p>
            </div>
          )}

          {/* Receipt email — only for card payments */}
          {!isPending && (
            <div className="mt-6 text-left" data-testid="qs-success-receipt-section">
              {receiptState === 'sent' ? (
                <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-center">
                  <p className="text-sm text-green-700 flex items-center justify-center">
                    <Mail className="w-4 h-4 mr-1" /> Receipt sent to {receiptEmail || prefillEmail}
                  </p>
                </div>
              ) : receiptState === 'sending' ? (
                <div className="text-center py-2"><Loader2 className="w-4 h-4 animate-spin text-gray-400 mx-auto" /></div>
              ) : (
                !prefillEmail && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 text-center">Get a receipt emailed to you</p>
                    <div className="flex space-x-2">
                      <Input type="email" placeholder="your@email.com" value={receiptEmail}
                        onChange={(e) => setReceiptEmail(e.target.value)}
                        className="h-9 text-sm flex-1" data-testid="qs-success-email-input" />
                      <Button size="sm" disabled={!receiptEmail.includes('@')}
                        onClick={() => sendReceipt(receiptEmail)}
                        className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-3" data-testid="qs-success-send-receipt-btn">
                        <Send className="w-3 h-3 mr-1" /> Send
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* Account CTA — lightweight, optional */}
          {!isPending && (
            <div className="mt-6 pt-5 border-t border-gray-100" data-testid="qs-success-account-cta">
              <p className="text-xs text-gray-400 mb-3">Track future payments and splits with Payo</p>
              <Button variant="outline" className="w-full h-10 text-sm border-blue-200 text-blue-700 hover:bg-blue-50"
                onClick={() => window.location.href = '/'} data-testid="qs-success-create-account-btn">
                Create your Payo account
              </Button>
              <p className="text-xs text-gray-400 mt-2">Optional - no account needed to pay</p>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-6">You can close this page</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickSplitSuccessPage;
